import { UIOverlayViewStatus } from 'common-ts/dist/models/UIOverlayViewStatus';

import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { resetUserMeasurementDraft } from './MeasurementsRedux';
import ProductOnUser from 'common-ts/dist/models/Product';
import { Order } from 'common-ts/dist/models/Orders';
import axios from 'axios';

export enum UniversalSizeActiveScreen {
    BenefitStart,
    MethodIntro,
    SizeSelect,
    SizeAdjust,
    BenefitMiddle,
    ResultLoading,
    Result,
    BenefitEnd,
    CheckSignedIn,

    SizeSelectServiceabilityLoading,
    SizeSelectServiceabilityAdjust,
    EditFull,
    LengkapiUkuran,
}

export interface UniversalSizeFlow {
    status: UIOverlayViewStatus,
    currentScreen: UniversalSizeActiveScreen,

    productDetail: ProductOnUser | undefined,
    flowVariant: string,
    flowInputVariables: any | undefined,

    marketplaceOrder: Order | undefined,
}

export const fetchMarketplaceOrder = createAsyncThunk(
  'overlayUniversalSize/fetchMarketplaceOrder', 
  async (payload: string, thunkAPI) => {

  const response = await axios({
    method: 'get',
    url: 'https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/order-public',
    params: {
      orderId: payload
    }
  });

  return response.data;
});

export const openOverlayCreateMeasWithInitialization = createAsyncThunk(
  'overlayUniversalSize/openOverlayCreateMeasWithInitialization', 
  async (payload: {
    productOnUser: ProductOnUser | undefined,
    flowVariant: 
      'productDetailInstant' | // Minified version of the flow, used when starting from product detail. Has no progress bar
      'measKitScan' | // Standard measurement kit version
      'measKitScanMarketplace' | // Marketplace measurement kit version - CURRENTLY NOT USED
      'itemQrScan' | // Item label scan version, item is already selected
      'itemQrScanMarketplace' | // Marketplace measurement kit version - CURRENTLY NOT USED
      'advanced' | // Same as General version, but opens up extra variables
      'default', // General version
    flowInputVariables?: any,
  }, thunkAPI) => {

  thunkAPI.dispatch(resetUserMeasurementDraft());
  thunkAPI.dispatch(openOverlay(payload));

  return null;
});

export const closeOverlayAndResetWithCleanup = createAsyncThunk(
  'overlayUniversalSize/closeOverlayAndResetWithCleanup', 
  async (payload, thunkAPI) => {

  thunkAPI.dispatch(resetUserMeasurementDraft());
  thunkAPI.dispatch(closeOverlayAndReset());

  return null;
});

// Define the initial state using that type
const initialState: UniversalSizeFlow = {
    status: UIOverlayViewStatus.OffAndReady,
    currentScreen: UniversalSizeActiveScreen.SizeSelect,

    productDetail: undefined,
    flowVariant: 'default',
    flowInputVariables: undefined,

    marketplaceOrder: undefined,
}

export const overlayUniversalSize = createSlice({
    name: 'overlayUniversalSize',
    initialState,
    reducers: {
        closeOverlayAndReset: (state) => {
            state.status = UIOverlayViewStatus.OffAndReady;
            state.currentScreen = UniversalSizeActiveScreen.SizeSelect;

            state.productDetail = undefined;
            state.flowVariant = 'default';
            state.flowInputVariables = undefined;

            state.marketplaceOrder = undefined;
        },

        openOverlay: (state, action: PayloadAction<{
            productOnUser: ProductOnUser | undefined,
            flowVariant?: string,
            flowInputVariables?: any,
          }>) => {
            state.status = UIOverlayViewStatus.On;
            state.productDetail = action.payload.productOnUser;
            state.flowInputVariables = action.payload.flowInputVariables;
            state.currentScreen = 
              action?.payload?.flowVariant === 'measKitScan' ? 
              UniversalSizeActiveScreen.BenefitStart :
              UniversalSizeActiveScreen.SizeSelect;
            state.flowVariant = 
              action?.payload?.flowVariant ? action.payload.flowVariant : 
              'default';
        },

        updateCurrentScreen: (state, action: PayloadAction<UniversalSizeActiveScreen>) => {
            state.currentScreen = action.payload;
        },
    },
    extraReducers(builder) {
      builder
      .addCase(fetchMarketplaceOrder.pending, (state, action) => {
        state.marketplaceOrder = undefined;
      })
      .addCase(fetchMarketplaceOrder.fulfilled, (state, action) => {
        state.marketplaceOrder = action.payload;
      })
      .addCase(fetchMarketplaceOrder.rejected, (state, action) => {
      });
    }
})

// Action creators are generated for each case reducer function
export const { closeOverlayAndReset, openOverlay, updateCurrentScreen } = overlayUniversalSize.actions

export default overlayUniversalSize.reducer